import { getEnvironment } from './environment';
import { Tag } from './tag';
import {
  getLastAncestor,
  getNodeHeight,
  getNodeWidth,
  restoreStyles,
  saveStyles,
  walkDomTree,
} from './utils/dom';
import { AdvertPayload } from './@types/ad-payload';
import { toBoolean } from './utils/util';
import { Constraints } from './@types/constraints';

type Size = {
  width: number;
  height: number;
};

type Creative = AdvertPayload['creative'];

/**
 * Default number of levels from our container upwards we will traverse and
 * resize to fit our creative if necessary.
 */
const DEFAULT_RESIZE_HEIGHT_DEPTH = 5;
const DEFAULT_RESIZE_WIDTH_DEPTH = 0;

export const getContainerDimension = (tag: Tag) =>
  `${parseInt(tag.container!.style.width, 10)}x${parseInt(
    tag.container!.style.height,
    10,
  )}`;

/**
 * Get the current display height, or safe frame display height
 * if available
 */
export const getDisplayHeight = () => {
  const { safeFrame, activeWindow } = getEnvironment();

  if (safeFrame) {
    const { self } = safeFrame.ext.geom();
    return Number(self.b) - Number(self.t);
  }

  const {
    innerHeight,
    screen: { height },
  } = activeWindow!;

  return Math.min(innerHeight, height);
};

/**
 * get the current display width, or safe frame display width
 * if available
 */
export const getDisplayWidth = () => {
  const { safeFrame, activeWindow } = getEnvironment();

  if (safeFrame) {
    const { self } = safeFrame.ext.geom();
    return Number(self.r) - Number(self.l);
  }

  const {
    innerWidth,
    screen: { width },
  } = activeWindow!;

  return Math.min(innerWidth, width);
};

/**
 * Given a Size object {w, h} apply any width and height constraints from the options.constraints property.
 * The size.w will be the smaller of constraints.width and size.w because we must not push content horizontally.
 * The size.h will be size.h because we can push content vertically.
 */
const getSizeConstraints = (size: Size, constraints: Constraints = {}) => {
  const maxSize = { width: size.width, height: size.height };

  if (constraints.width) {
    maxSize.width = Number(constraints.width);
  }
  if (constraints.height) {
    maxSize.height = Number(constraints.height);
  }

  return maxSize;
};

/**
 * Work out the maximum size we have available for our ads
 * based on the container/environment dimensions
 */
export const getPageSizeDetails = ({
  container,
  pageOptions: { constraints },
}: Tag) => {
  const { safeFrame, isUnfriendly } = getEnvironment();
  const containerParentBody = container!.ownerDocument.body;
  const lastAncestor =
    getLastAncestor(container!, constraints.target) || containerParentBody;

  /**
   * Use display size to make sure the dimensions we request for ads
   * aren't bigger than the screen
   */
  const displayHeight = getDisplayHeight();
  const displayWidth = getDisplayWidth();

  if (safeFrame) {
    const { self, exp, win } = safeFrame.ext.geom();

    // get current width by subtracting left from right
    const selfWidth = Number(self.r) - Number(self.l);

    // get possible dimensions we can expand to by working out width and height
    const expandedWidth = Number(exp.l) + Number(exp.r);
    const expandedHeight = Number(exp.b) + Number(exp.t);
    const maxSize = {
      width: Math.min(selfWidth + expandedWidth, Number(win.w)),
      height: Math.min(displayHeight + expandedHeight, Number(win.h)),
    };

    return {
      lastAncestor,
      initialMaxSize: maxSize,
      constrainedMaxSize: getSizeConstraints(maxSize, constraints),
      displaySize: maxSize,
    };
  }

  // We need to make sure that we use a sensible body height. There have been issues (e.g. amp pages)
  // where we don't get the correct body height, so sense check it.
  const bodyHeight = containerParentBody.clientHeight;
  const bodyWidth = containerParentBody.clientWidth;
  const maxSize = {
    width: !!constraints.useDisplayWidth
      ? bodyWidth > 0 && isUnfriendly
        ? Math.min(bodyWidth, displayWidth)
        : displayWidth
      : lastAncestor.clientWidth,
    height:
      bodyHeight > 0 && isUnfriendly
        ? Math.min(bodyHeight, displayHeight)
        : displayHeight,
  };

  return {
    lastAncestor,
    initialMaxSize: maxSize,
    constrainedMaxSize: getSizeConstraints(maxSize, constraints),
    displaySize: {
      width: displayWidth,
      height: displayHeight,
    },
  };
};

/**
 * Compare 2 size object {w, h}, return true if sz1 fits inside sz2 (ie. sz2 >= sz1).
 * Note: the back end returns w/h not width/height - bit legacy, but we can fix that
 * once we fix the back end too
 *
 * @param {Size} sz1
 * @param {Size} sz2
 * @returns {Boolean}
 */
export function fitsIn(
  sz1: {
    w: number;
    h: number;
  },
  sz2: {
    w: number;
    h: number;
  },
) {
  return sz1.w <= sz2.w && sz1.h <= sz2.h;
}

/**
 * Fit the src size object {w: int, h: int} into the dst size maintaining aspect ratio, returning
 * a new Size object with.
 * @param {Size} source
 * @param {Size} destination
 * @returns {Size}
 */
export function fit(source: Size, destination: Size) {
  let ratio = Math.min(
    destination.width / source.width,
    destination.height / source.height,
  );
  return {
    width: Math.round(source.width * ratio),
    height: Math.round(source.height * ratio),
  };
}
/**
 * Given a size object {w, h} find the largest creative size that will fit in
 * maxSize.
 * @param {Size} maxSize
 * @returns {Size}
 */
export const findLargestCreativeSizeThatFits = (
  creative: AdvertPayload['creative'],
  maxSize: Size,
) => {
  var size = { w: 0, h: 0 };
  creative.sizes.forEach((sz) => {
    if (
      fitsIn(sz, { w: maxSize.width, h: maxSize.height }) &&
      fitsIn(size, sz)
    ) {
      size.w = sz.w;
      size.h = sz.h;
    }
  });

  return { width: size.w, height: size.h };
};

export const getCreativeSizeByCreativeType = (
  creative: Creative,
  initialSize: Size,
  maxSize: Size,
  displaySize: Size,
): { width: number; height: number; creative?: Size } => {
  if (creative.sizing === 'fixed') {
    return initialSize;
  }

  if (creative.sizing === 'responsive') {
    // if no size then it's fully responsive
    if (!initialSize.width || !initialSize.height) {
      return maxSize;
    }

    if (creative.clazz.match(/CrossScreen/i)) {
      if (creative.proFormat) {
        return {
          ...fit(initialSize, maxSize),
          creative: fit(initialSize, {
            height: maxSize.height,
            width: displaySize.width,
          }),
        };
      }

      // Standard Unibanner
      if (creative.scaleUnit) {
        return fit(initialSize, maxSize);
      }

      return {
        width: maxSize.width,
        height: initialSize.height,
      };
    }

    if (creative.clazz.match(/Interscroller/i)) {
      return maxSize;
    }
  }

  return {
    width: 0,
    height: 0,
  };
};

export const resizeFriendlyContainers = (
  { pageOptions, container }: Tag,
  creativeSize: Size,
) => {
  const environment = getEnvironment();
  if (environment.frameElement || !environment.isIframe) {
    const constraints = pageOptions.constraints;

    let resizeHeightDepth =
      constraints?.resizeHeightDepth ||
      // deprecating 'resizeDepth' due to introduction of resizeHeightDepth - backwards compatibility until all adjusted
      constraints?.resizeDepth ||
      DEFAULT_RESIZE_HEIGHT_DEPTH;
    let resizeWidthDepth =
      constraints?.resizeWidthDepth || DEFAULT_RESIZE_WIDTH_DEPTH;

    // Resize container heights
    if (resizeHeightDepth > 0) {
      walkDomTree(container!.parentElement, (node) => {
        restoreStyles(node, ['min-height']);
        if (getNodeHeight(node) < creativeSize.height) {
          saveStyles(node, ['min-height']);
          node.style.minHeight = `${creativeSize.height}px`;
        }
        return --resizeHeightDepth <= 0;
      });
    }

    // Resize container widths
    if (resizeWidthDepth > 0) {
      walkDomTree(container!.parentElement, (node) => {
        restoreStyles(node, ['min-width']);
        if (getNodeWidth(node) < creativeSize.width) {
          saveStyles(node, ['min-width']);
          node.style.minWidth = `${creativeSize.width}px`;
        }

        return --resizeWidthDepth <= 0;
      });
    }
  }
};

export const resizeSafeFrameContainer = ({ width, height }: Size) => {
  const { safeFrame } = getEnvironment();
  if (!safeFrame) {
    return;
  }

  const { ext } = safeFrame;
  const {
    self: { w, h },
  } = ext.geom();

  const xOffset = width - Number(w);
  const yOffset = height - Number(h);

  if (xOffset > 0 || yOffset > 0) {
    ext.expand({
      r: xOffset,
      b: yOffset,
      push: true,
    });
  }
};

const centerContainer = (tag: Tag, lastAncestor: HTMLElement) => {
  const environment = getEnvironment();
  const constraints = tag.pageOptions.constraints;
  if (environment.safeFrame || toBoolean(constraints?.noCenter, false)) {
    return;
  }
  const container = tag.container!;

  container.style.margin = '0';

  const containerRect = container.getBoundingClientRect();
  const lastAncestorRect = lastAncestor.getBoundingClientRect();

  const lastAncestorCenterX =
    lastAncestorRect.left + lastAncestorRect.width / 2;
  const containerRectCenterX = containerRect.left + containerRect.width / 2;

  let marginX = lastAncestorCenterX - containerRectCenterX;
  container.style.margin = `0 0 0 ${marginX}px`;
};

/**
 * DOM mutation galore. This is the main entry for resizing the advert on
 * the page and we walk through several steps depending on iframe/creative types:
 *
 * Figure out our container size
 * Figure out the page size
 * Figure out the largest creative size that can fit
 * Get the creative size by the creative type
 * Set our container size to the sizes based above
 * Set our creative container size based on above (this may be different to
 * the container size depending on the implementation)
 * Resize our parent containers so that we fit and center ourselves
 */
export const resizeAdvert = (tag: Tag) => {
  const { payload, destroyed, container, creativeContainer } = tag;
  const creative = payload!.creative;

  if (destroyed) {
    return;
  }

  if (!container || !creativeContainer) {
    return;
  }

  const containerRect = container!.getBoundingClientRect();

  if (creative.clazz.match(/Interstitial/i)) {
    container.style.width = '1px';
    container.style.height = '1px';
    container.style.overflow = 'hidden';
    container.style.margin = `0 0 0 -${containerRect.left - 1}px`;

    return;
  }

  const { lastAncestor, constrainedMaxSize, displaySize } =
    getPageSizeDetails(tag);

  const initialCreativeSize = findLargestCreativeSizeThatFits(
    creative,
    constrainedMaxSize,
  );
  const creativeSize = getCreativeSizeByCreativeType(
    creative,
    initialCreativeSize,
    constrainedMaxSize,
    displaySize,
  );

  container.style.width = `${creativeSize.width}px`;
  container.style.height = `${creativeSize.height}px`;

  // For forcing pro size formats to the right width, we need to use the display width
  // the creative container is an absolute element, we set the max width and height,
  // but to the actual container we only set the height to push content vertically (safe)
  // but not the width to avoid causing havoc on stretching the publisher page
  if (creativeSize.creative) {
    container.style.height = `${creativeSize.creative.height}px`;
    creativeContainer.style.width = `${creativeSize.creative.width}px`;
    creativeContainer.style.height = `${creativeSize.creative.height}px`;
  }

  resizeFriendlyContainers(tag, creativeSize);
  resizeSafeFrameContainer(creativeSize);
  centerContainer(tag, lastAncestor);

  return tag;
};
